/**
 * useObjectCode - Hook para gestionar la codificación de objetos en el mapa
 *
 * @version 1.0
 * @updated 2026-02-18
 * @module core/coding
 *
 * @description
 * Maneja la generación de código para objetos nuevos y la detección de
 * re-codificación cuando un objeto existente cambia de celda H3 canónica.
 *
 * @remarks
 * Flujo para objeto NUEVO:
 *   1. En handleSave, llamar a generateCode() → retorna código del backend
 *
 * Flujo para objeto EXISTENTE movido a otra celda:
 *   1. useEffect detecta cambio de coordenadas vs originales
 *   2. Llama a checkRecode (read-only, no reserva código)
 *   3. Si willRecode===true, expone recodeStatus para mostrar aviso
 *   4. En handleSave, llamar a confirmRecode() → registra cambio en historial
 */

import { useState, useEffect, useCallback } from 'react';
import { codingApi } from './coding-api.service';
import type { CheckRecodeResult } from './coding-api.service';

/**
 * Opciones de configuración del hook
 */
export interface UseObjectCodeOptions {
  /** Código de tipo de objeto (e.g. "TR") */
  objectType: string;
  /** ID del proyecto activo */
  projectId: string | null;
  /** true si el objeto aún no fue persistido en BD */
  isNewObject: boolean;
  /** Código actual del objeto (solo para objetos existentes) */
  currentCode?: string;
  /** ID del objeto en su tabla de dominio (solo para objetos existentes) */
  objectId?: string;
  /** Coordenadas actuales del formulario [lng, lat] */
  coordinates: [number, number] | null;
  /** Coordenadas originales al abrir el panel [lng, lat] */
  originalCoordinates?: [number, number] | null;
}

/**
 * Resultado del hook
 */
export interface UseObjectCodeResult {
  /**
   * Estado de re-codificación cuando el objeto fue movido a otra celda.
   * null si no hay cambio de celda o el objeto es nuevo.
   */
  recodeStatus: CheckRecodeResult | null;
  /** true mientras se verifica si el movimiento cambia el código */
  isCheckingRecode: boolean;
  /**
   * Genera y retorna el código para un objeto nuevo.
   * Llamar en handleSave solo para isNewObject===true.
   * @returns Código generado (e.g. "TR_AA001_005") o null si falla
   */
  generateCode: () => Promise<string | null>;
  /**
   * Confirma el cambio de código para un objeto existente movido.
   * Incrementa el contador e inserta en el historial.
   * Llamar en handleSave solo cuando recodeStatus.willRecode===true.
   * @returns Nuevo código definitivo o null si falla
   */
  confirmRecode: () => Promise<string | null>;
}

/**
 * Hook para gestionar la codificación de objetos del mapa
 *
 * @param options - Configuración del objeto a gestionar
 * @returns Estado y funciones de codificación
 *
 * @example
 * ```tsx
 * const { recodeStatus, generateCode, confirmRecode } = useObjectCode({
 *   objectType: 'TR',
 *   projectId,
 *   isNewObject: isNewTree,
 *   currentCode: tree.code,
 *   objectId: tree.id,
 *   coordinates: formData.coordinates,
 *   originalCoordinates: tree.coordinates,
 * });
 * ```
 */
export function useObjectCode({
  objectType,
  projectId,
  isNewObject,
  currentCode,
  objectId,
  coordinates,
  originalCoordinates,
}: UseObjectCodeOptions): UseObjectCodeResult {
  const [recodeStatus, setRecodeStatus] = useState<CheckRecodeResult | null>(null);
  const [isCheckingRecode, setIsCheckingRecode] = useState(false);

  // Extraer valores primitivos para el dependency array de useEffect
  const newLng = coordinates?.[0] ?? null;
  const newLat = coordinates?.[1] ?? null;
  const origLng = originalCoordinates?.[0] ?? null;
  const origLat = originalCoordinates?.[1] ?? null;

  /**
   * Detecta automáticamente si las coordenadas cambiaron de celda.
   * Solo aplica a objetos existentes.
   */
  useEffect(() => {
    // Solo verificar para objetos existentes con todos los datos necesarios
    if (isNewObject || !projectId || !currentCode || newLng === null || newLat === null) {
      setRecodeStatus(null);
      return;
    }

    // Si las coordenadas no cambiaron, limpiar estado
    if (newLng === origLng && newLat === origLat) {
      setRecodeStatus(null);
      return;
    }

    let cancelled = false;
    setIsCheckingRecode(true);

    codingApi
      .checkRecode({
        objectType,
        currentCode,
        newLat,
        newLng,
        projectId,
      })
      .then((result) => {
        if (!cancelled) setRecodeStatus(result);
      })
      .catch(() => {
        // No bloquear la edición si la API falla (e.g. sin malla canónica)
        if (!cancelled) setRecodeStatus(null);
      })
      .finally(() => {
        if (!cancelled) setIsCheckingRecode(false);
      });

    return () => {
      cancelled = true;
    };
  }, [newLng, newLat, origLng, origLat, isNewObject, projectId, currentCode, objectType]);

  /**
   * Genera el código para un objeto nuevo al momento de guardar.
   */
  const generateCode = useCallback(async (): Promise<string | null> => {
    if (!projectId || newLng === null || newLat === null) return null;
    try {
      const result = await codingApi.generateCode({
        objectType,
        lat: newLat,
        lng: newLng,
        projectId,
      });
      return result.code;
    } catch (error) {
      console.error('[useObjectCode] Error al generar código:', error);
      return null;
    }
  }, [objectType, projectId, newLng, newLat]);

  /**
   * Confirma y registra el cambio de código de un objeto movido.
   */
  const confirmRecode = useCallback(async (): Promise<string | null> => {
    if (!projectId || !currentCode || !objectId || newLng === null || newLat === null) return null;
    try {
      const result = await codingApi.recordRecode({
        objectId,
        objectType,
        currentCode,
        newLat,
        newLng,
        projectId,
        reason: 'user_move',
      });
      return result.newCode;
    } catch (error) {
      console.error('[useObjectCode] Error al registrar cambio de código:', error);
      return null;
    }
  }, [objectType, projectId, currentCode, objectId, newLng, newLat]);

  return { recodeStatus, isCheckingRecode, generateCode, confirmRecode };
}
